import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { ConfirmCtx } from './useConfirm';

// Replaces window.confirm()/window.prompt() with a themed dialog that matches
// the rest of the app. useConfirm() resolves a boolean; usePrompt() resolves
// the entered string or null if cancelled — both return a Promise so call
// sites read the same as the native APIs did (`if (!(await confirm(...))) return`).
// The two hooks themselves live in ./useConfirm.js.
//
// Requests are held in a FIFO queue rather than a single slot: a second
// confirm()/prompt() opened while one is still on screen used to overwrite the
// pending resolver, leaving the first `await` to hang forever. Now every
// request keeps its own resolver, the head of the queue is the dialog shown,
// and answering it resolves that one promise and advances to the next — so no
// caller is ever stranded.
export function ConfirmProvider({ children }) {
  const [queue, setQueue] = useState([]); // pending requests; queue[0] is on screen
  const [draft, setDraft] = useState('');
  const idRef = useRef(0);

  const enqueue = useCallback((req) => new Promise((resolve) => {
    idRef.current += 1;
    const id = idRef.current;
    setQueue((q) => [...q, { ...req, id, resolve }]);
  }), []);

  const confirm = useCallback((message, opts = {}) =>
    enqueue({ kind: 'confirm', message, confirmLabel: opts.confirmLabel || 'Confirm', cancelLabel: opts.cancelLabel || 'Cancel', danger: !!opts.danger }), [enqueue]);

  const prompt = useCallback((message, opts = {}) =>
    enqueue({ kind: 'prompt', message, confirmLabel: opts.confirmLabel || 'Create', defaultValue: opts.defaultValue || '', allowEmpty: !!opts.allowEmpty }), [enqueue]);

  const active = queue[0] || null;
  const confirmBtnRef = useRef(null);

  // Reset the input draft each time a new prompt reaches the front of the queue.
  useEffect(() => {
    if (active && active.kind === 'prompt') setDraft(active.defaultValue || '');
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = (value) => {
    if (active) active.resolve(value);
    setQueue((q) => q.slice(1));
  };

  return (
    <ConfirmCtx.Provider value={{ confirm, prompt }}>
      {children}
      {active && (
        <Modal key={active.id} onClose={() => finish(active.kind === 'confirm' ? false : null)} labelledBy="confirm-dialog-message"
          // Focus the confirm button for an ordinary confirm so Enter/Space
          // accepts it (it used to land on Cancel). A prompt focuses its input
          // (autoFocus below); a danger confirm deliberately leaves focus on
          // Cancel so a reflexive Enter can't fire a destructive action.
          initialFocusRef={active.kind === 'confirm' && !active.danger ? confirmBtnRef : undefined}
          // Don't let an accidental backdrop click silently discard typed
          // prompt text; Escape and Cancel still dismiss deliberately.
          dismissOnBackdrop={!(active.kind === 'prompt' && draft.trim())}
          panelClassName="chrome-signal-indigo bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl w-full max-w-sm shadow-2xl shadow-black/10 dark:shadow-black/40 p-5">
          <div className="flex items-start gap-3">
            {active.danger && (
              <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-950/50 flex items-center justify-center shrink-0">
                <AlertTriangle size={16} className="text-red-500" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p id="confirm-dialog-message" className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-line">{active.message}</p>
              {active.kind === 'prompt' && (
                <input
                  autoFocus
                  aria-labelledby="confirm-dialog-message"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (draft.trim() || active.allowEmpty)) finish(draft.trim()); }}
                  className="mt-3 w-full bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-sm text-neutral-800 dark:text-neutral-100 outline-hidden focus:border-signal-indigo"
                />
              )}
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => finish(active.kind === 'confirm' ? false : null)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg text-muted dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors">
              {active.cancelLabel || 'Cancel'}
            </button>
            <button
              ref={confirmBtnRef}
              onClick={() => finish(active.kind === 'confirm' ? true : draft.trim())}
              disabled={active.kind === 'prompt' && !active.allowEmpty && !draft.trim()}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg text-white transition-colors disabled:opacity-40 ${
                active.danger ? 'bg-red-600 hover:bg-red-500' : 'bg-signal-indigo hover:opacity-90'}`}>
              {active.confirmLabel}
            </button>
          </div>
        </Modal>
      )}
    </ConfirmCtx.Provider>
  );
}
